import React from "react";
import { defineMessages, FormattedMessage as T, useIntl } from "react-intl";
import { makeStyles, Theme, darken } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import Dialog from "@material-ui/core/Dialog";
import IconButton from "@material-ui/core/IconButton";
import Typography from "@material-ui/core/Typography";
import ClearIcon from "@material-ui/icons/Clear";
import moment from "moment";

import {
  CityConfig,
  ControlReview,
  FineValueReq,
  FineValueReqTicket,
  Fps,
  FpsCountry,
  FpsId,
  FpsService,
  Sector,
  Ticket,
  Zone,
  FpsSignificantRight,
} from "@egis-smartcity/parking";
import { ControlFormValues } from "../ControlForm";
import { LocationControlFormValues } from "../LocationControl";
import FullscreenNotification, {
  FullscreenNotificationProps,
} from "../../FullscreenNotification";
import useCityConfig from "../../../hooks/useCityConfig";
import useControlReview from "../../../hooks/useControlReview/";
import useAuth from "../../../hooks/useAuth";
import useFps from "../../../hooks/useFps";

declare interface FineValueRes {
  statementDatetime?: string | null;
  validityDatetime?: string | null;
  finePrice?: number | null;
  reducedFinePrice?: number | null;
  reducedDatetime?: string | null;
  significantRights?: FpsSignificantRight[] | null;
}

const useStyles = makeStyles((theme: Theme) => ({
  /* Styles applied to the root element. */
  root: {
    // Use !important to override the Modal inline-style.
    zIndex: `${theme.zIndex.appBar - 1} !important` as unknown as number,
  },
  dialogTitle: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    color: theme.palette.common.white,
    backgroundColor: "#f18335",
    padding: "8px 16px",
  },
  closeIcon: {
    position: "absolute",
    top: 0,
    right: 0,
  },
  dialogContent: {
    minWidth: 300,
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    backgroundColor: theme.palette.common.white,
    borderRadius: `${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0 0`,
    padding: "8px 16px",
    "& >:nth-child(odd)": {
      paddingRight: "8px",
    },
  },
  infoContainer: {
    width: "50%",
    minHeight: "40px",
    "& >:first-child": {
      height: "100%",
      width: "100%",
      display: "flex",
      alignItems: "center",
      color: theme.palette.grey[700],
      borderBottom: "1px solid #111111",
      "& >*": {
        flex: 1,
        margin: 0,
        textAlign: "left",
      },
      "& >:nth-child(2)": {
        fontWeight: 600,
      },
    },
  },
  subInfos: {
    display: "flex",
    alignItems: "center",
    width: "100%",
    paddingRight: "0px !important",
    marginTop: "8px",
    "& > *": {
      flex: 1,
      fontWeight: 600,
      margin: "6px 0",
    },
    "& >:first-child": {
      textAlign: "left",
    },
    "& >:last-child": {
      textAlign: "right",
    },
  },
  nextFPS: {
    width: "100%",
    backgroundColor: theme.palette.common.white,
    color: theme.palette.grey[600],
    textAlign: "center",
    margin: "0px",
    padding: "8px 16px",
  },
  loader: {
    color: "#f18335",
  },
  dialogActions: {
    height: "60px",
    display: "flex",
    backgroundColor: "white",
    alignItems: "stretch",
    padding: "8px 16px",
    "& > *": {
      flex: 1,
    },
  },
  paperDialog: {
    borderRadius: theme.shape.borderRadius,
    backgroundColor: "#f18335",
  },
  confirmButton: {
    backgroundColor: "#f18335",
    "&:hover": {
      backgroundColor: darken("#f18335", 0.1),
    },
    marginLeft: 4,
  },
  cancelButton: {
    color: "white",
    backgroundColor: "#6c6c6c",
    marginRight: 4,
    "&:hover": {
      backgroundColor: darken("#6c6c6c", 0.1),
    },
  },
  errorFinePriceNull: {
    width: "100%",
    backgroundColor: theme.palette.common.white,
    color: theme.palette.error.main,
    textAlign: "center",
    margin: "0px",
    padding: "8px 16px",
    fontStyle: "italic",
  },
}));

const msg = defineMessages({
  fpsDialogTitle: {
    id: "fpsDialog.fpsDialogTitle.label",
    description: "Label of the modal.",
    defaultMessage: "Emit an FPS",
  },
  fpsNotifSuccess: {
    id: "fpsDialog.fpsNotifSuccess.message",
    description: "Message of the success notification.",
    defaultMessage: "Action emitted successfully.",
  },
  fpsNotifError: {
    id: "fpsDialog.fpsNotifError.message",
    description: "Message of the error notification.",
    defaultMessage:
      "Cannot issue the action. Please check the information entered.",
  },
  emitFpsFailureNotif: {
    id: "fpsDialog.emitNfpsFailureNotif.message",
    description: "Message of the emit FPS failure notification.",
    defaultMessage:
      "Enable to send an FPS, you will receive a new control in a few seconds.",
  },
  fpsLicencePlate: {
    id: "fpsDialog.fpsLicencePlate.label",
    description: "Label of the licence plate info.",
    defaultMessage: "Licence plate",
  },
  fpsBrand: {
    id: "fpsDialog.fpsBrand.label",
    description: "Label of the brand info.",
    defaultMessage: "Brand",
  },
  fpsModel: {
    id: "fpsDialog.fpsModel.label",
    description: "Label of the model info.",
    defaultMessage: "Model",
  },
  fpsPrice: {
    id: "fpsDialog.fpsPrice.label",
    description: "Label of the FPS price info.",
    defaultMessage: "FPS price",
  },
  fpsReducedPrice: {
    id: "fpsDialog.fpsReducedPrice.label",
    description: "Label of the Reduced FPS price info.",
    defaultMessage: "Reduced FPS price",
  },
  fpsZone: {
    id: "fpsDialog.fpsZone.label",
    description: "Label of the zone info.",
    defaultMessage: "Zone",
  },
  fpsSector: {
    id: "fpsDialog.fpsSector.label",
    description: "Label of the sector info.",
    defaultMessage: "Sector",
  },
  fpsStatementDate: {
    id: "fpsDialog.fpsStatementDate.label",
    description: "Label of the statement date info.",
    defaultMessage: "Date",
  },
  fpsStatementTime: {
    id: "fpsDialog.fpsStatementTime.label",
    description: "Label of the statement time info.",
    defaultMessage: "Hour",
  },
  fpsAddress: {
    id: "fpsDialog.fpsAddress.label",
    description: "Label of the address info.",
    defaultMessage: "Address",
  },
  fpsFreeDateEnd: {
    id: "fpsDialog.fpsFreeDateEnd.label",
    description: "Label of the free date end info.",
    defaultMessage: "Time when a new FPS can be applied:",
  },
  fpsCalculError: {
    id: "fpsDialog.fpsCalculError.label",
    description: "Notification message when FPS calcul failed.",
    defaultMessage: "FPS calcul failed. Please check the information entered.",
  },
  confirmButton: {
    id: "fpsDialog.confirmButton.label",
    description: "Label of the confirm button.",
    defaultMessage: "Emit FPS",
  },
  cancelButton: {
    id: "fpsDialog.cancelButton.label",
    description: "Label of the cancel button.",
    defaultMessage: "Cancel",
  },
  newFpsMessage: {
    id: "fpsDialog.newFpsMessage.label",
    description: "message that display next time for a new fps",
    defaultMessage: "A next FPS can be emited the :",
  },
  newFpsMessageAt: {
    id: "fpsDialog.newFpsMessageAt.label",
    description: "message between date and hour",
    defaultMessage: " at ",
  },
  errorFinePriceNull: {
    id: "fpsDialog.errorFinePriceNull",
    description: "message when error 1006 on fine price null",
    defaultMessage: "Null or negative FPS, please create an NFPS.",
  },
});

function createFps(
  cityConfig: CityConfig,
  cityId: string,
  controlFormValues: ControlFormValues,
  controlReview: ControlReview,
  fineLegalId: string,
  fineValue: FineValueRes,
  locationControlFormValues: LocationControlFormValues,
  zoneId: string,
  parkId?: string
): Fps {
  if (fineValue.finePrice === undefined || fineValue.finePrice === null) {
    throw new Error("fineValue.finePrice undefined.");
  }
  if (
    fineValue.validityDatetime === undefined ||
    fineValue.validityDatetime === null
  ) {
    throw new Error("fineValue.validityDatetime undefined.");
  }
  if (controlReview.control?.date === undefined) {
    throw new Error("controlReview.control.date undefined.");
  }

  const fps: Fps = {
    agent: {
      name: `${cityConfig.agent.familyName} ${cityConfig.agent.givenName}`,
      agentId: cityConfig.agent.agentId,
      worksFor: cityConfig.fps.worksFor,
    },
    authId: cityConfig.agent.agentId,
    cityId: cityId,
    fineLegalId: fineLegalId,
    finePrice: fineValue.finePrice,
    licensePlate: {
      plate: controlFormValues.licensePlate,
      plateCountry: controlFormValues.country.value as FpsCountry,
    },
    notificationAuthority: cityConfig.fps.notificationAuthority,
    parkId: parkId,
    paymentStatus: "PENDING",
    recourseOrganization: cityConfig.fps.recourseOrganization,
    reducedDatetime: fineValue.reducedDatetime || undefined,
    reducedFinePrice: fineValue.reducedFinePrice || undefined,
    significantRights: fineValue.significantRights || undefined,
    statementDatetime: controlReview.control.date,
    statementAddress: {
      streetNumber: cityConfig.review.dedicatedStreetNumber
        ? locationControlFormValues.streetNumber
        : undefined,
      streetName: locationControlFormValues.address,
      postalCode: locationControlFormValues.postalCode,
      addressLocality: locationControlFormValues.city,
      addressCountry: "FR",
    },
    terminalId:
      //@ts-ignore
      cityConfig.review.fps.terminalIdOverride || cityConfig.device.terminalId,
    type: "INITIAL",
    validityDatetime: fineValue.validityDatetime,
    vehicle: {
      brand: controlFormValues.carBrand?.label ?? "IGNOREE",
      model: controlFormValues.carModel?.label ?? "IGNOREE",
    },
    zoneId: zoneId,
  };

  cityConfig.fps.vehicleCategoryHack === true
    ? (fps.vehicle.vehicleCategory = controlFormValues.vehicleType.value)
    : (fps.vehicle.vehiculeCategory = controlFormValues.vehicleType.value);

  if (
    locationControlFormValues.lat !== null &&
    locationControlFormValues.lon !== null
  ) {
    fps.statementLocation = {
      latitude: locationControlFormValues.lat,
      longitude: locationControlFormValues.lon,
    };
  }

  return fps;
}

async function getNextFineLegalId(
  fpsService: FpsService,
  cityId: string,
  cityConfig: CityConfig,
  date: string
) {
  try {
    const nextFpsNumber = fpsService.getNextId(
      cityId,
      cityConfig?.fps?.indicativeAuthority,
      undefined,
      date
    );
    return nextFpsNumber;
  } catch (error) {
    throw error;
  }
}

function ticketToFineValueReqTicket(
  ticket: Ticket,
  sectors: Sector[],
  zones: Zone[],
  cityId: string
): FineValueReqTicket {
  let fineValueReqTicket: FineValueReqTicket = {
    cityId: cityId,
    endDatetime: ticket.endDateTime,
    rightPrice: ticket.amountPaid,
    startDatetime: ticket.startDateTime,
    type: ticket.profile === "FPS" ? "FINE" : "TICKET",
  };

  if (ticket.tariffCode) {
    fineValueReqTicket.zoneId =
      zones.find((zone) => zone.ticketZoneId === ticket.tariffCode)?.zoneId ||
      ticket.tariffCode;
  }

  if (ticket.locationCode) {
    fineValueReqTicket.parkId =
      sectors.find((sector) => sector.ticketParkId === ticket.locationCode)
        ?.parkId || ticket.locationCode;
  }

  return fineValueReqTicket;
}

export type FpsDialogProps = {
  /**
   * State of the dialog.
   */
  open: boolean;
  /**
   * Data of the current control.
   */
  controlReview: ControlReview;
  /**
   * The control form values.
   */
  controlForm: ControlFormValues;
  /**
   * The location control form values.
   */
  locationControlFormValues: LocationControlFormValues;
  /**
   * url of the plate picture.
   */
  plateImage: string;
  /**
   * url of the selected pictures.
   */
  imageSelection: string[];
  /**
   * Catch event when close button is clicked.
   */
  onCloseDialog(dialog: "fps"): void;
  /**
   * Handle when an FPS is succesfully send.
   */
  onFpsSent(force?: number): void;
  tickets: Ticket[];
};

/**
 * Dialog form to emit an FPS.
 *
 * @param props - The FpsDialog component props.
 */
function FpsDialog(props: FpsDialogProps) {
  ///////////////// PROPS, HOOKS AND STATE SETUP////////////////////////////////

  const {
    open,
    controlReview,
    controlForm,
    locationControlFormValues,
    plateImage,
    imageSelection,
    onCloseDialog,
    onFpsSent,
    tickets,
  } = props;
  const { formatMessage: t } = useIntl();
  const { cityConfig, sectors, zones } = useCityConfig();
  const { controlReviewService } = useControlReview();
  const { cityId } = useAuth();
  const { fpsService } = useFps();
  const [fpsCalcul, setFpsCalcul] = React.useState<FineValueRes>();
  const [loading, setLoading] = React.useState(false);
  const [isFinePriceNull, setIsFinePriceNull] = React.useState(false);
  const [errorCount, setErrorCount] = React.useState(0);
  const [notification, setNotification] =
    React.useState<FullscreenNotificationProps>({ open: false });
  const disabled = React.useMemo(() => loading, [loading]);

  const reset = React.useCallback(() => {
    setLoading(false);
    // setErrorCount(0);
    setNotification({ open: false });
    setFpsCalcul(undefined);
    setIsFinePriceNull(false);
  }, []);

  ///////////////// CSS ////////////////////////////////////////////////////////

  const classes = useStyles();

  ///////////////// INIT AND SIDE EFFECT(S) ////////////////////////////////////

  // If the errorCount is greater than 2 -> request a new control.
  React.useEffect(() => {
    if (errorCount === 2) {
      setErrorCount(42); // Just to be sure we don't run this effect anymore.

      const timeoutId = setTimeout(() => {
        reset();
        onFpsSent(1);
      }, 3000);

      setNotification({
        open: true,
        message: t(msg.emitFpsFailureNotif),
        type: "error",
        onCloseButtonClick: (event: React.SyntheticEvent<Element, Event>) => {
          clearTimeout(timeoutId);
          reset();
          onFpsSent(1);
        },
      });
      onCloseDialog("fps");
    }
  }, [errorCount, onCloseDialog, onFpsSent, reset, t]);

  // Compute the fine value.
  React.useEffect(() => {
    if (
      !open ||
      !fpsService ||
      !controlReview.control ||
      !cityId ||
      !zones ||
      !sectors
    )
      return;
    if (!cityConfig) {
      if (process.env.NODE_ENV !== "production") {
        console.error("cityConfig not available ATM.");
      }
      setLoading(false);
      return;
    }

    const currentSector = sectors.find(
      (sector) => sector.code === locationControlFormValues.sector.value
    ) as unknown as Sector;

    const currentZone = zones.find(
      (zone) => zone.code === locationControlFormValues.zone.value
    ) as unknown as Zone;

    const control: FineValueReq = {
      authId: cityConfig.agent.agentId,
      cityId,
      licensePlate: {
        plate: controlForm.licensePlate,
        plateCountry: controlForm.country.value,
      },
      parkId: currentSector.parkId,
      statementDatetime: controlReview.control.date,
      tickets: tickets
        .filter(
          (ticket) =>
            //@ts-ignore
            !cityConfig?.review?.ticket?.typeSynchro.includes(ticket.profile)
        )
        .map((ticket) =>
          ticketToFineValueReqTicket(ticket, sectors, zones, cityId)
        ),
      vehicle: {
        brand: controlForm.carBrand?.label || "IGNOREE",
        model: controlForm.carModel?.label || "IGNOREE",
      },
      zoneId: currentZone.zoneId,
    };

    cityConfig.fps.vehicleCategoryHack === true
      ? (control.vehicle.vehicleCategory = controlForm.vehicleType.value)
      : (control.vehicle.vehiculeCategory = controlForm.vehicleType.value);

    const computeFineValue = (i: number = 0) => {
      if (i === 2) {
        setErrorCount(2);
        return;
      }

      setLoading(true);
      fpsService
        .calcFps(cityId, control)
        .then((fineValueRes) => {
          setFpsCalcul(fineValueRes);
          if (fineValueRes.finePrice !== undefined)
            setIsFinePriceNull(fineValueRes.finePrice < 1);
        })
        .catch((error) => {
          if (process.env.NODE_ENV !== "production") console.error(error);
          const timeoutId = setTimeout(() => {
            setNotification({ open: false });
            setErrorCount((prev) => ++prev);
            onCloseDialog("fps");
          }, 3000);

          setNotification({
            open: true,
            message: t(msg.fpsCalculError),
            type: "error",
            onCloseButtonClick: (
              event: React.SyntheticEvent<Element, Event>
            ) => {
              clearTimeout(timeoutId);
              setNotification({ open: false });
              setErrorCount((prev) => ++prev);
              onCloseDialog("fps");
            },
          });
        })
        .finally(() => setLoading(false));
    };

    computeFineValue();
  }, [
    cityConfig,
    cityId,
    controlForm.carBrand,
    controlForm.carModel,
    controlForm.country.value,
    controlForm.licensePlate,
    controlForm.vehicleType.value,
    controlReview.control,
    fpsService,
    locationControlFormValues.sector.value,
    locationControlFormValues.zone.value,
    onCloseDialog,
    open,
    sectors,
    t,
    tickets,
    zones,
  ]);

  ///////////////// WORKFLOW ///////////////////////////////////////////////////

  const handleClose = React.useCallback(() => {
    reset();
    onCloseDialog("fps");
  }, [onCloseDialog, reset]);

  const onSubmit = React.useCallback(async () => {
    setLoading(true);

    let nextFineLegalId: FpsId;

    // Try to get the next `fineLegalId`.
    try {
      if (!fpsService) {
        if (process.env.NODE_ENV !== "production") {
          console.error("fpsService not available ATM.");
        }
        setLoading(false);
        return;
      }
      if (!cityId) {
        if (process.env.NODE_ENV !== "production") {
          console.error("cityId not available ATM.");
        }
        setLoading(false);
        return;
      }
      if (!cityConfig) {
        if (process.env.NODE_ENV !== "production") {
          console.error("cityConfig not available ATM.");
        }
        setLoading(false);
        return;
      }

      nextFineLegalId = await getNextFineLegalId(
        fpsService,
        cityId,
        cityConfig,
        controlReview.control?.date as string
      );
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error(error);
      setLoading(false);

      const timeoutId = setTimeout(() => {
        setNotification({ open: false });
        setErrorCount((prev) => ++prev);
      }, 3000);

      setNotification({
        open: true,
        message: t(msg.fpsNotifError),
        type: "error",
        onCloseButtonClick: (event: React.SyntheticEvent<Element, Event>) => {
          clearTimeout(timeoutId);
          setNotification({ open: false });
          setErrorCount((prev) => ++prev);
        },
      });
      return;
    }

    // Try to emit the FPS.
    try {
      if (!cityConfig) {
        if (process.env.NODE_ENV !== "production") {
          console.error("cityConfig not available ATM.");
        }
        setLoading(false);
        return;
      }
      if (!zones) {
        if (process.env.NODE_ENV !== "production") {
          console.error("zones not available ATM.");
        }
        setLoading(false);
        return;
      }
      if (!cityId) {
        if (process.env.NODE_ENV !== "production") {
          console.error("cityId not available ATM.");
        }
        setLoading(false);
        return;
      }
      if (!fpsCalcul) {
        if (process.env.NODE_ENV !== "production") {
          console.error("fpsCalcul not available ATM.");
        }
        setLoading(false);
        return;
      }
      const currentZone = zones.find(
        (zone) => zone.code === locationControlFormValues.zone.value
      ) as unknown as Zone;
      const currentSector = sectors?.find(
        (sector) => sector.code === locationControlFormValues.sector.value
      );
      const fps: Fps = createFps(
        cityConfig,
        cityId,
        controlForm,
        controlReview,
        nextFineLegalId.id,
        fpsCalcul,
        locationControlFormValues,
        currentZone.zoneId,
        currentSector?.parkId
      );

      if (!controlReviewService) {
        if (process.env.NODE_ENV !== "production") {
          console.error("controlReviewService not available ATM.");
        }
        setLoading(false);
        return;
      }
      if (!controlReview.controlId) {
        throw new Error("controlReview.controlId undefined.");
      }
      await controlReviewService.emitFps(controlReview.controlId, {
        fpsData: fps,
        mediasUrl: [plateImage, ...imageSelection],
      });

      const timeoutId = setTimeout(() => {
        reset();
        onFpsSent(1);
      }, 5000);

      setNotification({
        open: true,
        message: t(msg.fpsNotifSuccess),
        type: "success",
        onCloseButtonClick: (event: React.SyntheticEvent<Element, Event>) => {
          clearTimeout(timeoutId);
          reset();
          onFpsSent(1);
        },
      });
      onCloseDialog("fps");
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error(error);
      setLoading(false);
      const timeoutId = setTimeout(() => {
        setNotification({ open: false });
        setErrorCount((prev) => ++prev);
      }, 3000);
      setNotification({
        open: true,
        message: t(msg.fpsNotifError),
        type: "error",
        onCloseButtonClick: (event: React.SyntheticEvent<Element, Event>) => {
          clearTimeout(timeoutId);
          setNotification({ open: false });
          setErrorCount((prev) => ++prev);
        },
      });

      return;
    } finally {
      setLoading(false);
    }
  }, [
    cityConfig,
    cityId,
    controlForm,
    controlReview,
    controlReviewService,
    fpsCalcul,
    fpsService,
    imageSelection,
    locationControlFormValues,
    onCloseDialog,
    onFpsSent,
    plateImage,
    reset,
    sectors,
    t,
    zones,
  ]);

  return (
    <>
      <Dialog
        className={classes.root}
        classes={{ paper: classes.paperDialog }}
        onClose={handleClose}
        open={open}
        disableBackdropClick
        disableEscapeKeyDown
        disablePortal
      >
        <div className={classes.dialogTitle}>
          <Typography variant="h6">
            <T {...msg.fpsDialogTitle} />
          </Typography>
          {controlReview.control?.date !== undefined && (
            <>
              <Typography variant="subtitle1">
                <small>{moment(controlReview.control.date).format("L")}</small>
              </Typography>
              <Typography variant="subtitle2">
                <b>{moment(controlReview.control.date).format("LTS")}</b>
              </Typography>
            </>
          )}
          <IconButton
            className={classes.closeIcon}
            color="inherit"
            disabled={disabled}
            onClick={handleClose}
          >
            <ClearIcon fontSize="large" />
          </IconButton>
        </div>
        <div className={classes.dialogContent}>
          <div className={classes.subInfos}>
            <p>{t(msg.fpsPrice)}</p>
            <p>{fpsCalcul?.finePrice ? fpsCalcul?.finePrice / 100 : 0}€</p>
          </div>
          <div className={classes.subInfos}>
            <p>{t(msg.fpsReducedPrice)}</p>
            <p>
              {fpsCalcul?.reducedFinePrice
                ? fpsCalcul.reducedFinePrice / 100
                : 0}
              €
            </p>
          </div>
        </div>
        <p className={classes.nextFPS}>
          {t(msg.newFpsMessage)}{" "}
          {fpsCalcul?.validityDatetime
            ? moment(fpsCalcul.validityDatetime).format("DD/MM/YYYY")
            : "?"}
          {t(msg.newFpsMessageAt)}
          {fpsCalcul?.validityDatetime
            ? moment(fpsCalcul.validityDatetime).format("HH:mm")
            : "?"}
        </p>
        {isFinePriceNull && cityConfig?.review.fps.msgParkingAllowed && (
          <p className={classes.errorFinePriceNull}>
            {t(msg.errorFinePriceNull)}
          </p>
        )}
        <div className={classes.dialogActions}>
          <Button
            className={classes.cancelButton}
            size="small"
            variant="contained"
            disabled={disabled}
            onClick={handleClose}
          >
            <T {...msg.cancelButton} />
          </Button>
          <Button
            className={classes.confirmButton}
            color="primary"
            variant="contained"
            onClick={onSubmit}
            disabled={disabled || isFinePriceNull}
            startIcon={
              loading && (
                <CircularProgress className={classes.loader} size={20} />
              )
            }
          >
            <T {...msg.confirmButton} />
          </Button>
        </div>
      </Dialog>
      <FullscreenNotification {...notification} />
    </>
  );
}

export default FpsDialog;
